import type { Mention } from './extraction';
import type { FlagType } from './flags';
import { containsPhrase } from './text';

export type DropReason =
  'about_other' | 'negated' | 'figurative' | 'hypothetical' | 'media' | 'long_ago';

export interface DroppedMention {
  readonly mention: Mention;
  readonly reason: DropReason;
}

export interface MentionTriage {
  /** Real, current, confident and quoted verbatim from the parent → urgent candidates. */
  readonly redFlags: readonly FlagType[];
  /** Real but unverified, low-confidence or up to a month old → never silent, not an emergency. */
  readonly attentionFlags: readonly FlagType[];
  /** Filtered out, kept for audit and evals. */
  readonly dropped: readonly DroppedMention[];
}

function dropReason(mention: Mention): DropReason | null {
  if (mention.about === 'other') return 'about_other';
  if (mention.negated) return 'negated';
  if (mention.mode !== 'real') return mention.mode;
  if (mention.when === 'long_ago') return 'long_ago';
  return null;
}

/**
 * Turns the model's annotated mentions into flags. Rules, not the model, handle the traps:
 * "the neighbour fell", "I didn't fall", "nearly fell off the chair laughing", "afraid to fall",
 * a fall in a TV series, a fall five years ago. A quote that is not found verbatim in the
 * parent's own words (paraphrased, invented, or taken from Vera's question) is downgraded to
 * attention instead of being trusted.
 */
export function triageMentions(mentions: readonly Mention[], parentSpeech: string): MentionTriage {
  const red = new Set<FlagType>();
  const attention = new Set<FlagType>();
  const dropped: DroppedMention[] = [];

  for (const mention of mentions) {
    const reason = dropReason(mention);
    if (reason) {
      dropped.push({ mention, reason });
      continue;
    }
    const grounded = containsPhrase(parentSpeech, mention.evidence);
    if (grounded && mention.confidence === 'high' && mention.when !== 'recent') {
      red.add(mention.type);
    } else {
      attention.add(mention.type);
    }
  }

  return {
    redFlags: [...red],
    attentionFlags: [...attention].filter((flag) => !red.has(flag)),
    dropped,
  };
}
